import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { useTranslation } from "react-i18next";
import type { WithdrawalRequestPayload } from "../types/api";

type CryptoAsset = "USDT" | "BTC" | "ETH" | "BNB";
type CryptoNetwork = "TRC20" | "ERC20" | "BTC" | "BSC";
type WithdrawMethod = "crypto" | "card";

interface WithdrawFormModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (data: WithdrawalRequestPayload) => Promise<void>;
  availableBalance: number;
  isProcessing?: boolean;
}

const networkForAsset = (asset: CryptoAsset): CryptoNetwork =>
  asset === "BTC" ? "BTC" : asset === "BNB" ? "BSC" : asset === "ETH" ? "ERC20" : "TRC20";

const ibanPattern = /^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/;

const WithdrawFormModal = ({
  open,
  onClose,
  onConfirm,
  availableBalance,
  isProcessing = false,
}: WithdrawFormModalProps) => {
  const { t } = useTranslation();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState<WithdrawMethod>("crypto");
  const [asset, setAsset] = useState<CryptoAsset>("USDT");
  const [network, setNetwork] = useState<CryptoNetwork>("TRC20");
  const [destination, setDestination] = useState("");
  const [accountHolder, setAccountHolder] = useState("");

  useEffect(() => {
    if (!open) return;
    setStep(1);
    setAmount("");
    setMethod("crypto");
    setAsset("USDT");
    setNetwork("TRC20");
    setDestination("");
    setAccountHolder("");
  }, [open]);

  const handleClose = () => {
    if (isProcessing) return;
    onClose();
  };

  const numericAmount = Number.parseFloat(amount.replace(",", "."));
  const amountValid =
    !Number.isNaN(numericAmount) &&
    numericAmount >= 10 &&
    numericAmount <= availableBalance;

  const cleanDestination = destination.trim().replace(/\s+/g, "").toUpperCase();
  const destinationValid =
    method === "crypto" ? cleanDestination.length >= 10 : ibanPattern.test(cleanDestination);
  const cleanAccountHolder = accountHolder.trim();
  const accountHolderValid = method === "crypto" || cleanAccountHolder.length >= 3;
  const step2Valid = destinationValid && accountHolderValid;

  const handleAssetChange = (value: string) => {
    const nextAsset = value as CryptoAsset;
    setAsset(nextAsset);
    setNetwork(networkForAsset(nextAsset));
  };

  const handleMethodChange = (value: string) => {
    setMethod(value as WithdrawMethod);
    setDestination("");
    setAccountHolder("");
  };

  const handleSubmit = async () => {
    try {
      await onConfirm(
        method === "crypto"
          ? {
              amount: numericAmount,
              currency: "USD",
              method: "crypto",
              asset,
              network,
              destination: cleanDestination,
            }
          : {
              amount: numericAmount,
              currency: "USD",
              method: "card",
              destination: cleanDestination,
              accountHolder: cleanAccountHolder,
            }
      );
    } catch {
      // L'erreur est affichée par le parent (toast) ; le formulaire reste ouvert pour correction
    }
  };

  const maskedDestination =
    cleanDestination.length > 10
      ? `${cleanDestination.slice(0, 6)}…${cleanDestination.slice(-4)}`
      : cleanDestination;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-h-[90vh] max-w-md overflow-auto bg-card text-foreground rounded-3xl border border-border mx-auto dark:bg-slate-950/95 dark:text-white dark:border-white/10">
        <DialogHeader className="space-y-1 text-center">
          <DialogTitle className="text-2xl font-semibold">
            {t("accounts.withdrawModalTitle")}
          </DialogTitle>
        </DialogHeader>

        <div className="mb-2 flex items-center gap-1.5">
          {[1, 2, 3].map((s) => (
            <span
              key={s}
              className={`h-1 flex-1 rounded-full transition ${
                s <= step ? "bg-cyan-500" : "bg-muted dark:bg-white/10"
              }`}
            />
          ))}
        </div>

        {step === 1 && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t("accounts.withdrawAmountLabel")}</Label>
              <Input
                type="text"
                inputMode="decimal"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
                placeholder="0.00"
                className="bg-background text-foreground border-border dark:bg-white/5 dark:text-white"
              />
              <div className="flex justify-between text-xs text-muted-foreground dark:text-white/60">
                <span>{t("accounts.withdrawAvailableLabel")}</span>
                <span className="font-medium text-foreground dark:text-white">
                  {availableBalance.toFixed(2)} $
                </span>
              </div>
              {amount.length > 0 && !amountValid && (
                <p className="text-xs text-destructive">
                  {Number.isNaN(numericAmount)
                    ? t("accounts.withdrawAmountInvalid")
                    : numericAmount > availableBalance
                    ? t("accounts.withdrawAmountTooHigh")
                    : t("accounts.withdrawAmountTooLow")}
                </p>
              )}
            </div>
            <div className="flex gap-2">
              {[25, 50, 100].map((pct) => (
                <button
                  key={pct}
                  type="button"
                  onClick={() =>
                    setAmount(((availableBalance * pct) / 100).toFixed(2))
                  }
                  className="flex-1 rounded-lg border border-border bg-muted/60 py-1.5 text-xs font-semibold text-foreground transition hover:bg-muted dark:border-white/10 dark:bg-white/5 dark:text-white dark:hover:bg-white/10">
                  {pct === 100 ? t("accounts.withdrawMax") : `${pct}%`}
                </button>
              ))}
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="ghost" onClick={handleClose}>
                {t("accounts.cancel")}
              </Button>
              <Button
                type="button"
                disabled={!amountValid}
                onClick={() => setStep(2)}
                className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white hover:from-cyan-600 hover:to-blue-600">
                {t("accounts.continue")}
              </Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t("accounts.withdrawMethodLabel")}</Label>
              <Select value={method} onValueChange={handleMethodChange}>
                <SelectTrigger className="bg-background text-foreground border-border dark:bg-white/5 dark:text-white dark:border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="crypto">{t("accounts.withdrawMethodCrypto")}</SelectItem>
                  <SelectItem value="card">{t("accounts.withdrawMethodCard")}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {method === "crypto" ? (
              <>
                <div className="space-y-2">
                  <Label>{t("accounts.withdrawAssetLabel")}</Label>
                  <Select value={asset} onValueChange={handleAssetChange}>
                    <SelectTrigger className="bg-background text-foreground border-border dark:bg-white/5 dark:text-white dark:border-white/10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USDT">USDT</SelectItem>
                      <SelectItem value="BTC">BTC</SelectItem>
                      <SelectItem value="ETH">ETH</SelectItem>
                      <SelectItem value="BNB">BNB</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t("accounts.withdrawNetworkLabel")}</Label>
                  {asset === "USDT" ? (
                    <Select
                      value={network}
                      onValueChange={(value) => setNetwork(value as CryptoNetwork)}>
                      <SelectTrigger className="bg-background text-foreground border-border dark:bg-white/5 dark:text-white dark:border-white/10">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="TRC20">TRC20</SelectItem>
                        <SelectItem value="ERC20">ERC20</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-foreground/85 dark:border-white/10 dark:bg-white/5 dark:text-white/70">
                      {network}
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>{t("accounts.withdrawDestinationLabel")}</Label>
                  <Input
                    type="text"
                    value={destination}
                    onChange={(event) => setDestination(event.target.value)}
                    placeholder={t("accounts.withdrawCryptoDestinationPlaceholder")}
                    className="bg-background text-foreground border-border dark:bg-white/5 dark:text-white"
                  />
                  {destination.length > 0 && !destinationValid && (
                    <p className="text-xs text-destructive">
                      {t("accounts.withdrawDestinationInvalid")}
                    </p>
                  )}
                  <p className="text-xs text-amber-600 dark:text-amber-300/90">
                    {t("accounts.withdrawNetworkWarning")}
                  </p>
                </div>
              </>
            ) : (
              <>
                <div className="space-y-2">
                  <Label>{t("accounts.withdrawIbanLabel")}</Label>
                  <Input
                    type="text"
                    value={destination}
                    onChange={(event) => setDestination(event.target.value)}
                    placeholder={t("accounts.withdrawIbanPlaceholder")}
                    className="bg-background text-foreground border-border dark:bg-white/5 dark:text-white"
                  />
                  {destination.length > 0 && !destinationValid && (
                    <p className="text-xs text-destructive">
                      {t("accounts.withdrawIbanInvalid")}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>{t("accounts.withdrawAccountHolderLabel")}</Label>
                  <Input
                    type="text"
                    value={accountHolder}
                    onChange={(event) => setAccountHolder(event.target.value)}
                    placeholder={t("accounts.withdrawAccountHolderPlaceholder")}
                    className="bg-background text-foreground border-border dark:bg-white/5 dark:text-white"
                  />
                  {accountHolder.length > 0 && !accountHolderValid && (
                    <p className="text-xs text-destructive">
                      {t("accounts.withdrawAccountHolderInvalid")}
                    </p>
                  )}
                </div>
              </>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="ghost" onClick={() => setStep(1)}>
                {t("accounts.back")}
              </Button>
              <Button
                type="button"
                disabled={!step2Valid}
                onClick={() => setStep(3)}
                className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white hover:from-cyan-600 hover:to-blue-600">
                {t("accounts.continue")}
              </Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <dl className="divide-y divide-border overflow-hidden rounded-xl border border-border text-sm dark:divide-white/10 dark:border-white/10">
              <div className="flex justify-between px-3 py-2">
                <dt className="text-muted-foreground dark:text-white/60">
                  {t("accounts.withdrawAmountLabel")}
                </dt>
                <dd className="font-semibold">{numericAmount.toFixed(2)} $</dd>
              </div>
              <div className="flex justify-between px-3 py-2">
                <dt className="text-muted-foreground dark:text-white/60">
                  {t("accounts.withdrawMethodLabel")}
                </dt>
                <dd className="font-semibold">
                  {method === "crypto"
                    ? `${asset} (${network})`
                    : t("accounts.withdrawMethodCard")}
                </dd>
              </div>
              {method === "card" && (
                <div className="flex justify-between px-3 py-2">
                  <dt className="text-muted-foreground dark:text-white/60">
                    {t("accounts.withdrawAccountHolderLabel")}
                  </dt>
                  <dd className="font-semibold">{cleanAccountHolder}</dd>
                </div>
              )}
              <div className="flex justify-between px-3 py-2">
                <dt className="text-muted-foreground dark:text-white/60">
                  {t("accounts.withdrawDestinationLabel")}
                </dt>
                <dd className="font-semibold">{maskedDestination}</dd>
              </div>
              <div className="flex justify-between px-3 py-2">
                <dt className="text-muted-foreground dark:text-white/60">
                  {t("accounts.withdrawEtaLabel")}
                </dt>
                <dd className="font-semibold">{t("accounts.withdrawEtaValue")}</dd>
              </div>
            </dl>
            <p className="text-xs text-muted-foreground dark:text-white/50">
              {t("accounts.withdrawDisclaimer")}
            </p>
            <div className="flex justify-end gap-3 pt-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setStep(2)}
                disabled={isProcessing}>
                {t("accounts.back")}
              </Button>
              <Button
                type="button"
                disabled={isProcessing}
                onClick={handleSubmit}
                className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white hover:from-cyan-600 hover:to-blue-600">
                {isProcessing ? t("accounts.processing") : t("accounts.withdrawSubmit")}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default WithdrawFormModal;
